import axios from 'axios';

// Helper to get config safely
const getConfig = () => {
  return window.APP_CONFIG || {};
};

class DifyService {
  constructor() {
    this.instance = null;
  }

  // Lazy initialization
  getInstance() {
    if (!this.instance) {
      const config = getConfig();
      const apiKey = config.DIFY_API_KEY;
      
      this.instance = axios.create({
        baseURL: config.DIFY_API_URL || 'https://api.dify.ai/v1',
        timeout: 60000, // Dify might take longer for AI generation
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        }
      });

      this.instance.interceptors.response.use(
        response => {
          return response.data;
        },
        error => {
          console.error('Dify API Error:', error);
          return Promise.reject(error);
        }
      );
    }
    return this.instance;
  }

  /**
   * Run a workflow
   * @param {Object} inputs - The inputs for the workflow
   * @param {string} user - User identifier
   * @param {boolean} stream - Whether to stream response (default false)
   */
  async runWorkflow(inputs, user = 'default-user', stream = false) {
    try {
      const response = await this.getInstance().post('/workflows/run', {
        inputs,
        response_mode: stream ? 'streaming' : 'blocking',
        user
      });
      return response;
    } catch (error) {
      console.error('Failed to run Dify workflow:', error);
      throw error;
    }
  }

  /**
   * Send a message to a chat app
   * @param {string} query - The user query
   * @param {Object} inputs - Context inputs
   * @param {string} conversationId - Previous conversation ID
   * @param {string} user - User identifier
   * @param {boolean} stream - Whether to stream response
   */
  async sendMessage(query, inputs = {}, conversationId = '', user = 'default-user', stream = false) {
    try {
      const payload = {
        query,
        inputs,
        response_mode: stream ? 'streaming' : 'blocking',
        user,
        conversation_id: conversationId
      };
      
      // Filter out empty conversation_id to allow starting new conversation
      if (!conversationId) {
        delete payload.conversation_id;
      }

      const response = await this.getInstance().post('/chat-messages', payload);
      return response;
    } catch (error) {
      console.error('Failed to send Dify message:', error);
      throw error;
    }
  }
  
  /**
   * Upload file to Dify
   * @param {File} file - The file object
   * @param {string} user - User identifier
   */
  async uploadFile(file, user = 'default-user') {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('user', user);

      const response = await this.getInstance().post('/files/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response;
    } catch (error) {
      console.error('Failed to upload file to Dify:', error);
      throw error;
    }
  }
}

export default new DifyService();
